/**
 * ================================================================
 * USE PROFILE HOOK - User Profile Management
 * ================================================================
 *
 * TanStack Query hook for fetching and managing user profile data.
 * Extracted from auth.ts refreshUser function.
 *
 * @author MeBi Team
 * @version 1.0.0
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  profileService,
  type UserProfile,
} from "@/features/common/services/profile.service";

// Query keys
export const profileKeys = {
  all: ["profile"] as const,
  current: () => [...profileKeys.all, "current"] as const,
};

/**
 * Hook to fetch current user profile with full data
 */
export const useProfile = () => {
  return useQuery({
    queryKey: profileKeys.current(),
    queryFn: async (): Promise<UserProfile | null> => {
      try {
        // Return unified profile directly from service
        return await profileService.getCurrentUserProfile();
      } catch (error) {
        console.error("[useProfile] Fetch failed:", error);
        return null;
      }
    },
    staleTime: 0,
    retry: 1,
    refetchOnMount: true,
  });
};

/**
 * Hook to get available roles from profile
 */
export const useProfileRoles = () => {
  const { data: profile } = useProfile();
  return profileService.getSafeRoles(profile);
};

/**
 * Hook to refresh profile data
 */
export const useRefreshProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await queryClient.invalidateQueries({ queryKey: profileKeys.current() });
      return queryClient.fetchQuery({ queryKey: profileKeys.current() });
    },
  });
};
